import React from "react";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { isUserAuthorized, getCurrentTheme } from "./store/selectors";
import {
  checkToken,
  fetchUserProfile,
  loadInitialData,
} from "./actions/authActions";
import withClearCache from "./ClearCache";
import { ConfigProvider, Spin, theme } from "antd";
import { THEME } from "./constants";
import es_ES from "antd/es/locale/es_ES";
import { getCookie, setCookie } from "./utils";
import "mana-font/css/mana.css";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const App = () => {
  const dispatch = useDispatch();
  const currentTheme = useSelector(getCurrentTheme, shallowEqual);

  const ClearCacheComponent = withClearCache(() => {
    return <></>;
  });

  const { auth } = useSelector(isUserAuthorized) || {};

  const fetchInitialData = useCallback(() => {
    dispatch(fetchUserProfile());
    dispatch(loadInitialData());
  }, [dispatch]);

  const checkPersistency = useCallback(() => {
    dispatch(checkToken());
  }, [dispatch]);

  useEffect(() => {
    auth ? fetchInitialData() : checkPersistency();
  }, [auth, fetchInitialData, checkPersistency]);

  useEffect(() => {
    const refreshTheme = async () => {
      const theme = await getCookie(THEME);
      if (theme === undefined || theme === null) {
        await setCookie(THEME, true);
        await dispatch({ type: THEME, payload: true });
      } else {
        await dispatch({ type: THEME, payload: theme });
      }
    };
    refreshTheme();
  }, [dispatch]);

  const appSuspenseFallback = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <Spin size="large" />
    </div>
  );

  return (
    <Suspense fallback={appSuspenseFallback}>
      <ConfigProvider
        locale={es_ES}
        theme={{
          token: {
            borderRadius: 3,
            colorPrimary: "#d4a843",
            colorBgBase: currentTheme ? "#221d15" : "#221d15",
            colorBgLayout: currentTheme ? "#1a1510" : "#1a1510",
            colorBgContainer: currentTheme ? "#2e2820" : "#2e2820",
            colorBorder: currentTheme ? "rgba(212, 168, 67, 0.25)" : "rgba(212, 168, 67, 0.25)",
            colorBorderSecondary: currentTheme ? "rgba(212, 168, 67, 0.12)" : "rgba(212, 168, 67, 0.12)",
            colorText: currentTheme ? "#f4e8c1" : "#f4e8c1",
            colorTextSecondary: currentTheme ? "#8a7a60" : "#8a7a60",
            colorTextDisabled: currentTheme
              ? "rgba(212, 168, 67, 0.35)"
              : "rgba(212, 168, 67, 0.35)",
          },
          components: {
            Switch: {
              colorPrimary: "#d4a843",
              colorPrimaryHover: "#e8c96a",
            },
            Form: {
              labelColor: "#f4e8c1",
            },
            Input: {
              colorText: "#f4e8c1",
              colorTextPlaceholder: "#b7a984",
              colorBgContainer: "#2e2820",
              colorBorder: "rgba(212, 168, 67, 0.25)",
            },
            Select: {
              colorText: "#f4e8c1",
              colorTextPlaceholder: "#b7a984",
              colorBgContainer: "#2e2820",
              colorBorder: "rgba(212, 168, 67, 0.25)",
              optionSelectedColor: "#fff6df",
              optionActiveBg: "rgba(212, 168, 67, 0.2)",
              optionSelectedBg: "rgba(212, 168, 67, 0.28)",
            },
            Tabs: {
              itemColor: "#d8c9a4",
              itemSelectedColor: "#fff6df",
              itemHoverColor: "#fff1cd",
              inkBarColor: "#d4a843",
            },
            Modal: {
              titleColor: "#fff6df",
              contentBg: "#2e2820",
              headerBg: "#2e2820",
            },
          },
          algorithm: theme.darkAlgorithm,
        }}
      >
        <ClearCacheComponent />
        {auth ? <MainContainer /> : <AuthContainer />}
      </ConfigProvider>
    </Suspense>
  );
};

export default App;
